import { useCallback } from 'react'
import { Search, X } from 'lucide-react'
import type { BdrFiltersAll } from '@/types'
import {
  AllLeadsInlineSelect,
  AllLeadsSortIcon,
  type AllLeadsSelectOption,
} from '@/components/BdrPanelV2/bdrAllLeadsTableUtils'

interface BdrAllLeadsTableToolbarProps {
  localSearch: string
  setLocalSearch: (value: string) => void
  filters: BdrFiltersAll
}

const statusOptions: AllLeadsSelectOption[] = [
  { value: 'novo', label: 'Novo' },
  { value: 'sql', label: 'SQL' },
  { value: 'lead', label: 'Lead' },
]

const tempOptions: AllLeadsSelectOption[] = [
  { value: 'hot', label: 'Quente' },
  { value: 'warm', label: 'Morno' },
  { value: 'cold', label: 'Frio' },
]

const faseOptions: AllLeadsSelectOption[] = [
  { value: '1', label: 'Fase 1' },
  { value: '2', label: 'Fase 2' },
  { value: '3', label: 'Fase 3' },
  { value: '4', label: 'Fase 4' },
]

export function BdrAllLeadsTableToolbar({
  localSearch,
  setLocalSearch,
  filters,
}: BdrAllLeadsTableToolbarProps) {
  const hasActiveFilters =
    filters.filterStatus || filters.filterTemp || filters.filterFase || filters.search

  const clearFilters = useCallback(() => {
    setLocalSearch('')
    filters.setSearch('')
    filters.setFilterStatus('')
    filters.setFilterTemp('')
    filters.setFilterFase('')
  }, [filters, setLocalSearch])

  const toggleSort = useCallback(
    (field: string) => {
      if (filters.sortField === field) {
        filters.setSortDir(filters.sortDir === 'asc' ? 'desc' : 'asc')
      } else {
        filters.setSortField(field)
        filters.setSortDir('desc')
      }
    },
    [filters],
  )

  return (
    <div className="glass-card border-white/[0.06] p-4 mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder="Buscar lead..."
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-xs rounded-lg pl-9 pr-3 py-2 outline-none focus:border-accent transition-colors min-h-[44px] placeholder:text-text-muted"
          />
        </div>

        <AllLeadsInlineSelect
          value={filters.filterStatus}
          onChange={filters.setFilterStatus}
          options={statusOptions}
          placeholder="Status"
        />
        <AllLeadsInlineSelect
          value={filters.filterTemp}
          onChange={filters.setFilterTemp}
          options={tempOptions}
          placeholder="Temperatura"
        />
        <AllLeadsInlineSelect
          value={filters.filterFase}
          onChange={filters.setFilterFase}
          options={faseOptions}
          placeholder="Fase"
        />

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="min-w-[44px] min-h-[44px] flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-text-muted hover:text-text-primary hover:bg-white/[0.04] transition-colors"
          >
            <X size={14} />
            Limpar
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/[0.04]">
        <span className="text-text-muted text-[10px] uppercase tracking-wider">Ordenar:</span>
        {(['score', 'name', 'entrada'] as const).map((field) => (
          <button
            key={field}
            onClick={() => toggleSort(field)}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
              filters.sortField === field
                ? 'text-accent bg-accent/10'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            {field === 'score' ? 'Score' : field === 'name' ? 'Nome' : 'Entrada'}
            <AllLeadsSortIcon field={field} sortField={filters.sortField} sortDir={filters.sortDir} />
          </button>
        ))}
      </div>
    </div>
  )
}
